import { useState, useCallback, useEffect } from 'react';
import MetricCard from './components/MetricCard';
import RiskGauge from './components/RiskGauge';
import PortfolioForm from './components/PortfolioForm';
import AlertModal from './components/AlertModal';
import RateLimitMonitor from './components/RateLimitMonitor';
import ApiStatusIndicator from './components/ApiStatusIndicator';
import SettingsPanel from './components/SettingsPanel';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useMetrics, useAlerts, useTimeSince } from './hooks/useMetrics';
import { usePreferences } from './hooks/useLocalStorage';
import { getRecommendedAction } from './utils/riskCalculator';
import { useTranslation } from './hooks/useTranslation';

function App() {
  const { preferences, updatePreference } = usePreferences();
  const { t } = useTranslation();
  const [portfolioData, setPortfolioData] = useState(preferences.portfolio);
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [showActionDetails, setShowActionDetails] = useState(false);

  // Fetch metrics with user-configured refresh interval
  const { metrics, loading, error, lastUpdate, isStale, refresh } = useMetrics(
    preferences.refreshInterval,
    portfolioData
  );

  // Monitor for alerts with deduplication
  const handleAlert = useCallback((alert) => {
    // Create unique key for this alert
    const alertKey = `${alert.metric}-${alert.status}`;
    
    // Skip if already dismissed or already in alerts
    if (dismissedAlerts.has(alertKey)) {
      return;
    }
    
    // Check if this exact alert already exists
    const isDuplicate = alerts.some(
      existing => existing.metric === alert.metric && existing.status === alert.status
    );
    
    if (!isDuplicate) {
      setAlerts(prev => [...prev, alert]);
    }
  }, [alerts, dismissedAlerts]);

  // Clear dismissed alerts when metrics improve
  useEffect(() => {
    if (!metrics || !metrics.statuses) return;
    
    // Check if any dismissed alerts should be cleared (status improved to green)
    const updatedDismissed = new Set(dismissedAlerts);
    let hasChanges = false;
    
    Object.entries(metrics.statuses).forEach(([metricName, status]) => {
      if (status === 'green') {
        // Clear both yellow and red dismissals for this metric
        [`${metricName}-yellow`, `${metricName}-red`].forEach(key => {
          if (updatedDismissed.has(key)) {
            updatedDismissed.delete(key);
            hasChanges = true;
          }
        });
      }
    });
    
    if (hasChanges) {
      setDismissedAlerts(updatedDismissed);
    }
  }, [metrics, dismissedAlerts]);

  useAlerts(metrics, handleAlert);

  const timeSince = useTimeSince(lastUpdate);

  // Handle portfolio changes
  const handlePortfolioChange = useCallback((data) => {
    setPortfolioData(data);
    updatePreference('portfolio', data);
  }, [updatePreference]);

  // Get breached metrics for recommendations
  const breachedMetrics = metrics ? Object.entries(metrics.statuses || {})
    .filter(([, status]) => status === 'red' || status === 'yellow')
    .map(([metric]) => metric) : [];

  const recommendedAction = metrics 
    ? getRecommendedAction(metrics.compositeScore, breachedMetrics, t)
    : '';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('app.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('app.subtitle')}
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Last update indicator */}
              <div className="text-right">
                <div className={`text-xs font-medium ${isStale ? 'text-danger' : 'text-gray-600 dark:text-gray-400'}`}>
                  {isStale ? t('app.staleData') : t('app.lastUpdated')}
                </div>
                <div className="text-sm text-gray-800 dark:text-white">
                  {timeSince}
                </div>
              </div>

              {/* Refresh button */}
              <button
                onClick={refresh}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <svg 
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? t('app.refreshing') : t('app.refresh')}
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Settings button */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                title={t('app.settings')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error State */}
        {error && (
          <div className="bg-danger/10 border border-danger rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-danger mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-danger font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !metrics && (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-4">{t('common.loading')}</p>
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        {metrics && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Column: Risk Gauge & Portfolio */}
            <div className="lg:col-span-1 space-y-6">
              <RiskGauge 
                score={metrics.compositeScore} 
                title={t('risk.compositeScore')}
              />
              <PortfolioForm 
                onPortfolioChange={handlePortfolioChange}
                initialValues={portfolioData}
              />
              <ApiStatusIndicator />
            </div>

            {/* Right Column: Metrics */}
            <div className="lg:col-span-3 space-y-6">
              {/* Recommended Action */}
              {recommendedAction && (
                <div 
                  className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md ${
                    metrics.compositeScore < 30 
                      ? 'bg-success/10 border-success hover:bg-success/20'
                      : metrics.compositeScore < 70
                      ? 'bg-warning/10 border-warning hover:bg-warning/20'
                      : 'bg-danger/10 border-danger hover:bg-danger/20'
                  }`}
                  onClick={() => setShowActionDetails(true)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="mr-3">
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                          {t('action.recommended')}
                        </h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {recommendedAction}
                        </p>
                      </div>
                    </div>
                    <div className="text-gray-400 hover:text-gray-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <MetricCard
                  title={t('metrics.utilization')}
                  value={metrics.utilization}
                  status={metrics.statuses.utilization}
                  format="percentage"
                  description={t('metrics.utilizationDesc')}
                  history={metrics.history.utilization}
                  threshold={t('thresholds.utilization')}
                />

                <MetricCard
                  title={t('metrics.peg')}
                  value={metrics.peg?.deviation}
                  status={metrics.statuses.pegDeviation}
                  format="percentage"
                  description={t('metrics.pegDesc')}
                  history={metrics.history.peg}
                  threshold={t('thresholds.peg')}
                />

                <MetricCard
                  title={t('metrics.loopExposure')}
                  value={metrics.loopExposure}
                  status={metrics.statuses.loopExposure}
                  format="percentage"
                  description={t('metrics.loopExposureDesc')}
                  threshold={t('thresholds.loopExposure')}
                />

                <MetricCard
                  title={t('metrics.fundingRate')}
                  value={metrics.fundingRate}
                  status={metrics.statuses.fundingRate}
                  format="rate"
                  description={t('metrics.fundingRateDesc')}
                  history={metrics.history.fundingRate}
                  threshold={t('thresholds.fundingRate')}
                />

                <MetricCard
                  title={t('metrics.tvlChange')}
                  value={metrics.tvl?.change24h}
                  status={metrics.statuses.tvlChange}
                  format="rate"
                  description={t('metrics.tvlChangeDesc')}
                  history={metrics.history.tvl.map(p => ({ ...p, value: p.value / 1000000 }))}
                  threshold={t('thresholds.tvlChange')}
                />

                <MetricCard
                  title={t('metrics.tvlCurrent')}
                  value={metrics.tvl?.current}
                  status="green"
                  format="currency"
                  description={t('metrics.tvlCurrentDesc')}
                />
              </div>


              {/* Additional Info */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('about.title')}
                </h3>
                <div className="prose prose-sm dark:prose-invert text-gray-600 dark:text-gray-400">
                  <p className="mb-2">
                    {t('about.desc1')}
                  </p>
                  <p className="mb-2">
                    <strong>{t('about.desc2').split(':')[0]}:</strong> {t('about.desc2').split(':')[1]}
                  </p>
                  <p className="mb-2">
                    <strong>{t('about.desc3').split(':')[0]}:</strong> {t('about.desc3', { interval: preferences.refreshInterval / 1000 })}
                  </p>
                  <p className="mb-2">
                    <strong>{t('about.desc4').split(':')[0]}:</strong> {t('about.desc4').split(':')[1]}
                  </p>
                  <p>
                    <strong>{t('about.desc5').split(':')[0]}:</strong> {t('about.desc5').split(':')[1]}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Settings Panel */}
      <SettingsPanel 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        preferences={preferences}
        updatePreference={updatePreference}
      />

      {/* Alert Modal */}
      <AlertModal 
        alerts={alerts}
        onDismiss={(index) => {
          const dismissedAlert = alerts[index];
          if (dismissedAlert) {
            const alertKey = `${dismissedAlert.metric}-${dismissedAlert.status}`;
            setDismissedAlerts(prev => new Set([...prev, alertKey]));
          }
        }}
        onDismissAll={() => {
          // Add all current alerts to dismissed set
          alerts.forEach(alert => {
            const alertKey = `${alert.metric}-${alert.status}`;
            setDismissedAlerts(prev => new Set([...prev, alertKey]));
          });
          setAlerts([]);
        }}
      />

      {/* Action Details Modal */}
      {showActionDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('action.detailedAnalysis')}
                </h2>
                <button
                  onClick={() => setShowActionDetails(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Risk Summary */}
                <div className={`p-4 rounded-lg border-l-4 ${
                  metrics.compositeScore < 30 
                    ? 'bg-success/10 border-success'
                    : metrics.compositeScore < 70
                    ? 'bg-warning/10 border-warning'
                    : 'bg-danger/10 border-danger'
                }`}>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {t('action.riskSummary')}
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                    {recommendedAction}
                  </p>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('action.compositeScore')}: {metrics.compositeScore.toFixed(1)}/100
                  </div>
                </div>

                {/* Breached Metrics */}
                {breachedMetrics.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                      {t('action.breachedMetrics')} ({breachedMetrics.length})
                    </h3>
                    <div className="space-y-2">
                      {breachedMetrics.map(metric => (
                        <div key={metric} className="flex items-center justify-between text-sm">
                          <span className="capitalize text-gray-700 dark:text-gray-300">
                            {metric.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            metrics.statuses[metric] === 'red' 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                          }`}>
                            {metrics.statuses[metric] === 'red' ? t('status.red') : t('status.yellow')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommended Actions */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">
                    💡 {t('action.recommendedSteps')}
                  </h3>
                  <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                    {metrics.compositeScore < 30 ? (
                      <>
                        <li>• {t('action.step1')}</li>
                        <li>• {t('action.step2')}</li>
                      </>
                    ) : metrics.compositeScore < 70 ? (
                      <>
                        <li>• {t('action.step3')}</li>
                        <li>• {t('action.step4')}</li>
                        <li>• {t('action.step5')}</li>
                      </>
                    ) : (
                      <>
                        <li>• {t('action.step6')}</li>
                        <li>• {t('action.step7')}</li>
                        <li>• {t('action.step8')}</li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Portfolio Context */}
                {portfolioData && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                      {t('action.portfolioContext')}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">{t('portfolio.totalValue')}:</span>
                        <span className="ml-2 font-medium">${portfolioData.totalValue?.toLocaleString() || '0'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">{t('portfolio.exposureLabel')}:</span>
                        <span className="ml-2 font-medium">{portfolioData.exposurePercent || 0}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

